/**
 * Copy for the stage a long-running backend job reports while it works.
 *
 * Three actions take minutes — a batch validation run (a DUT fan-out across the
 * dairy roster), a batch recompute, and an RFS/LCFS report-suite render — and
 * each used to show a spinner and nothing else for the duration, which is
 * indistinguishable from wedged. The suite render was the worst of them: its
 * progress bar could not move until the first artifact was sealed, so a good
 * run sat at "0 of 7" for over seven minutes. Each now reports a coarse stage
 * from one backend vocabulary
 * (`compliance.shared.stages.RunStage`), and all three render it through here so
 * the screens cannot describe the same work with different words.
 *
 * What is shared is the stage vocabulary, the 409-adoption helpers, and the
 * rules for reading a job that has stopped — whether a failed status read
 * settles it, and whether a terminal state actually did the work. What
 * "finished" *says* is deliberately not: a finished validation reports when the
 * findings were last checked, a finished recompute reports that new figures were
 * published, and folding those into one component would make both worse.
 */

import { isApiError } from "@compliance/domain";

/** Backend `RunStage` value → the sentence shown under the spinner. */
const STAGE_COPY: Record<string, string> = {
  preparing: "Preparing — reading batch configuration",
  reading_telemetry: "Reading meter telemetry",
  reading_dairies: "Reading dairy telemetry",
  checking_coverage: "Checking hourly coverage",
  checking_rules: "Checking data-quality rules",
  rendering: "Building the report artifacts",
  saving: "Saving results",
  publishing: "Publishing the new revision",
};

/**
 * Stand-in for a stage this bundle does not know about.
 *
 * The backend can add a stage ahead of a deploy of this app, and showing
 * something generic keeps the line honest — it is still moving, we just cannot
 * name it. Dropping to no text at all would read as the run having stalled.
 */
const UNKNOWN_STAGE = "Working…";

/**
 * The sentence for a reported stage, or `null` when there is nothing to say.
 *
 * `null` covers both a run that has not reported yet and a row written before
 * the backend had the column; callers fall back to their own bare "running"
 * copy. A stage on a *finished* run means nothing — it is whatever the run last
 * reported — so read it only while the run is actually in flight.
 */
export function stageCopy(stage: string | null | undefined): string | null {
  if (!stage) return null;
  return STAGE_COPY[stage] ?? UNKNOWN_STAGE;
}

/**
 * Why the start button is refusing, while a run of each kind is in flight.
 *
 * Lives beside the stage vocabulary because it is the other half of the same
 * story: the stage line says what the run is doing, and this says why the button
 * that would start another one is greyed out. A disabled control that explains
 * nothing reads as a broken app or a role problem, and it is neither.
 *
 * Deliberately says nothing about the stage. That is already on screen a few
 * pixels below, and repeating it here would give the operator two copies to
 * reconcile when they disagree by one poll.
 */
export const VALIDATION_RUNNING_REASON =
  "Can't run — a validation run is already in progress for this batch. " +
  "It will finish on its own, and the findings below will refresh when it does.";

/** The same, for a recompute. See {@link VALIDATION_RUNNING_REASON}. */
export const RECOMPUTE_RUNNING_REASON =
  "Can't run — a recompute is already in progress for this batch. " +
  "It will finish on its own, and the figures below will refresh when it does.";

/**
 * The job id a 409 refusal is pointing at, or `null` if this is not that refusal.
 *
 * Starting a job that is already running is refused rather than duplicated, and
 * the refusal names the run in flight — so the right response is to attach to it,
 * not to show an error. There is nothing to retry: the work is already happening.
 */
export function runningJobIdFrom(err: unknown): string | null {
  return refusedIdFrom(err, "job_id");
}

/** The same, for validation — which is keyed on a run id rather than a job id. */
export function runningRunIdFrom(err: unknown): string | null {
  return refusedIdFrom(err, "run_id");
}

function refusedIdFrom(err: unknown, key: "job_id" | "run_id"): string | null {
  if (!isApiError(err)) return null;
  if (err.httpStatus !== 409 || err.code !== "CONCURRENT_MODIFICATION") return null;
  const id = err.details[key];
  return typeof id === "string" && id.length > 0 ? id : null;
}

/**
 * Did reading a job's status *settle* it, as opposed to failing to answer?
 *
 * A status read that fails definitively ends the run for the client: a stale or
 * bookmarked job id — expired, malformed, or belonging to a batch this user can
 * no longer see — would otherwise leave the handle parked in the URL forever,
 * the start button disabled forever, and the only advice on screen ("reload")
 * reproducing the same failure.
 *
 * `TIMEOUT`, 5xx, `RATE_LIMITED` and raw network failures are deliberately not
 * settlements: those are transient, and releasing the job id on one would
 * re-enable the button while the work is still running server-side, straight
 * into a duplicate-run race. Those keep polling.
 *
 * Shared by the two surfaces that follow a job, which had byte-identical copies
 * of this predicate — and which is why it sits here beside the stage vocabulary
 * and the 409-adoption helpers rather than in either component.
 */
export function jobStatusFailed(query: { isError: boolean; error: unknown }): boolean {
  if (!query.isError || !isApiError(query.error)) return false;
  if (query.error.code === "RATE_LIMITED") return false;
  return (
    (query.error.httpStatus >= 400 && query.error.httpStatus < 500) ||
    query.error.code === "CONTRACT_VIOLATION"
  );
}

/**
 * What a *terminal* recompute job's `result` says about whether figures moved.
 *
 * `superseded` carries the winner's id when there is one to follow; a skip with
 * nothing to follow is still a skip, and must not be reported as a recompute.
 */
export type RecomputeOutcome =
  | { kind: "recomputed" }
  | { kind: "superseded"; runningJobId: string | null };

/**
 * Read a finished recompute's `result` before believing its state.
 *
 * `state: "done"` is not evidence that the figures were rewritten. Two racing
 * recomputes both reach `done`: the loser yields after claiming and finishes
 * carrying `skipped: "superseded"` plus the winner's `running_job_id`, having
 * recomputed nothing. A client holding the loser's id that reads the state
 * alone announces a compliance action that did not happen, and refreshes onto
 * the *old* figures while the winner is still working.
 *
 * `running_job_id` decides on its own, without also matching on `skipped`: the
 * backend documents following it whenever it is present, and a skip reason
 * added later should route the same way rather than fall through to a false
 * success. A `skipped` with no id has nowhere to send us, so it is reported as
 * what it is.
 */
export function recomputeOutcome(
  result: Record<string, unknown> | null | undefined,
): RecomputeOutcome {
  if (!result) return { kind: "recomputed" };
  const id = result.running_job_id;
  if (typeof id === "string" && id.length > 0) {
    return { kind: "superseded", runningJobId: id };
  }
  if (typeof result.skipped === "string" && result.skipped.length > 0) {
    return { kind: "superseded", runningJobId: null };
  }
  return { kind: "recomputed" };
}
